import { IonIcon } from '@ionic/react';
import { trendingDown, trendingUp } from 'ionicons/icons';
import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';

const COLOR_GREEN = '#3E9D8F';
const COLOR_RED = '#BF4858';
const COLOR_GREEN_DARK = '#4CAF50';
const COLOR_RED_DARK = '#F44336';

const useStyles = createUseStyles({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeIn: {
    opacity: 0,
    animation: '$fadeIn 0.5s ease-in-out forwards',
  },
  hidden: {
    opacity: 0,
  },
  card: {
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    minHeight: '150px',
    padding: '15px',
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    transition: 'background-color 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',
    },
  },
  metricContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  primaryText: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#282828',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  secondaryText: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  valueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'baseline',
  },
  valueContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rightAlign: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  monthLabel: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#666666',
    margin: 0,
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  icon: {
    marginLeft: '5px',
    marginRight: '3px',
    width: '18px',
    height: '18px',
    alignSelf: 'center',
    '--ionicon-stroke-width': '50px',
    transition: 'color 0.3s ease',
  },
  iconGreen: {
    color: COLOR_GREEN,

    '@media (prefers-color-scheme: dark)': {
      color: COLOR_GREEN_DARK,
    },
  },
  iconRed: {
    color: COLOR_RED,

    '@media (prefers-color-scheme: dark)': {
      color: COLOR_RED_DARK,
    },
  },
  percentChangeText: {
    fontSize: '12px',
    fontWeight: 600,
    margin: 0,
    transition: 'color 0.3s ease',
  },
  percentGreen: {
    color: COLOR_GREEN,

    '@media (prefers-color-scheme: dark)': {
      color: COLOR_GREEN_DARK,
    },
  },
  percentRed: {
    color: COLOR_RED,

    '@media (prefers-color-scheme: dark)': {
      color: COLOR_RED_DARK,
    },
  },
});

interface TimeMetric {
  current: {
    value: string;
    unit: string;
  };
  previous: {
    value: string;
    unit: string;
  };
  percentChange: number;
}

interface ResponseTimesProps {
  data: {
    averageTimeToStart: TimeMetric;
    averageTimeToComplete: TimeMetric;
  };
  isLoading: boolean;
}

const ResponseTimes: React.FC<ResponseTimesProps> = ({
  data: { averageTimeToStart, averageTimeToComplete },
  isLoading,
}) => {
  const classes = useStyles();

  const contentClass = isLoading ? classes.hidden : classes.fadeIn;

  const currentMonth = useMemo(() => {
    return new Date().toLocaleString('en-US', { month: 'short' });
  }, []);

  const previousMonth = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toLocaleString('en-US', { month: 'short' });
  }, []);

  const isTimeToStartImproved = averageTimeToStart.percentChange < 0;
  const isTimeToCompleteImproved = averageTimeToComplete.percentChange < 0;

  return (
    <div className={classes.card} style={{ gap: '10px' }}>
      {/* Average time to start */}
      <div className={classes.metricContainer}>
        <p className={classes.secondaryText} style={{ marginBottom: '5px' }}>
          Average time to start
        </p>
        <div className={`${classes.valueRow} ${contentClass}`}>
          <div className={classes.valueContainer}>
            <div className={classes.leftGroup}>
              <h2 className={classes.primaryText}>
                {averageTimeToStart.current.value} {averageTimeToStart.current.unit}
              </h2>
              <IonIcon
                icon={isTimeToStartImproved ? trendingDown : trendingUp}
                className={`${classes.icon} ${
                  isTimeToStartImproved ? classes.iconGreen : classes.iconRed
                }`}
              />
              <p
                className={`${classes.percentChangeText} ${
                  isTimeToStartImproved ? classes.percentGreen : classes.percentRed
                }`}
              >
                {Math.abs(averageTimeToStart.percentChange)}%
              </p>
            </div>
            <span className={classes.monthLabel}>{currentMonth}</span>
          </div>
          <div className={classes.rightAlign}>
            <h2 className={classes.primaryText}>
              {averageTimeToStart.previous.value} {averageTimeToStart.previous.unit}
            </h2>
            <p className={classes.monthLabel}>{previousMonth}</p>
          </div>
        </div>
      </div>

      {/* Average time to complete */}
      <div className={classes.metricContainer}>
        <p className={classes.secondaryText} style={{ marginBottom: '5px' }}>
          Average time to complete
        </p>
        <div className={`${classes.valueRow} ${contentClass}`}>
          <div className={classes.valueContainer}>
            <div className={classes.leftGroup}>
              <h2 className={classes.primaryText}>
                {averageTimeToComplete.current.value} {averageTimeToComplete.current.unit}
              </h2>
              <IonIcon
                icon={isTimeToCompleteImproved ? trendingDown : trendingUp}
                className={`${classes.icon} ${
                  isTimeToCompleteImproved ? classes.iconGreen : classes.iconRed
                }`}
              />
              <p
                className={`${classes.percentChangeText} ${
                  isTimeToCompleteImproved ? classes.percentGreen : classes.percentRed
                }`}
              >
                {Math.abs(averageTimeToComplete.percentChange)}%
              </p>
            </div>
            <span className={classes.monthLabel}>{currentMonth}</span>
          </div>
          <div className={classes.rightAlign}>
            <h2 className={classes.primaryText}>
              {averageTimeToComplete.previous.value} {averageTimeToComplete.previous.unit}
            </h2>
            <p className={classes.monthLabel}>{previousMonth}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResponseTimes;
